<template>
  <DiscardChangesModal
    :open="showDiscardChangesModal"
    :modal_config="props.modal_config"
    @close-modal="showDiscardChangesModal = false"
    @confirm="emit('close-modal')"
  />
  <Modal :open="props.open" :hideClose="true">
    <template #modal-header>
      <div>
        <div class="flex items-center">
          <Icon name="Pencil" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Edit</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Edit Beneficial Owner
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-2 lg:p-6">
        <div>
          <TabView
            :tabs="props.modal_config.tabs"
            :activeTab="activeProfileTab"
            @update:activeTab="(key) => (activeProfileTab = key)"
          >
            <template
              v-for="subtab in props.modal_config.tabs"
              :key="subtab.slot_name"
              #[subtab.slot_name]
            >
              <div v-if="props.modal_config.groups?.includes(subtab.label)">
                <Form
                  :schema_array="
                    props.modal_config.schema.filter(
                      (item) => item.record_detail_group === subtab.label
                    )
                  "
                  :state_object="getStateObject(subtab.label)"
                  :record_id="props.modal_config.record_id"
                  variant="secondary"
                />
              </div>
            </template>
          </TabView>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="handleConfirmClose()" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// IMPORTS
import { ref, watch } from "vue";
import { Modal, TabView, Form, Icon, Button } from "@/components";
import { useCrudStore, useSchemaStore, useUIStore, useDataStore } from "@/stores";
import _ from "lodash";
import DiscardChangesModal from "./DiscardChangesModal.vue";

//STORES
const crudStore = useCrudStore();
const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const UIStore = useUIStore();

// EMITS & PROPS
const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

//REFS
const activeProfileTab = ref("personal.personal_information");
const showDiscardChangesModal = ref(false);
//WATCHERS
watch(
  () => props.modal_config.open,
  (is_open) => {
    if (is_open) {
      activeProfileTab.value = props.modal_config.tabs[0].slot_name;
    } else {
      schemaStore.show_input_errors = false;
    }
  },
  { immediate: true }
);

function handleConfirmClose() {
  let values_changed = checkForChanges();
  let id_info_changed = checkForChanges("id_info");
  if (!values_changed && !id_info_changed) {
    emit("close-modal");
    return;
  }

  // show discard changes modal
  showDiscardChangesModal.value = true;
}

// COMPONENT LOGIC AND FUNCTIONS
async function handleConfirm() {
  try {
    let values_changed = checkForChanges();
    let id_info_changed = checkForChanges("id_info");

    if (!values_changed && !id_info_changed) {
      emit("close-modal");
      return;
    }

    let object = props.modal_config.object_to_edit;
    // object.id_info = props.modal_config.id_info;

    let _continue = schemaStore.validateFields(
      object,
      props.modal_config.schema,
      props.modal_config.record_id,
      true,
      "beneficial_owner",
      props.modal_config.original_object
    );

    if (_continue.proceed) {
      object = schemaStore.addCreatedUpdatedFields(object, false, true);

      await crudStore.update(
        "contacts",
        [
          {
            field_name: "record_id",
            operator: "eq",
            value: props.modal_config.record_id,
          },
        ],
        object
      );

      await handleActivityLog(true);

      emit("confirm", {
        type: "beneficial_owner",
        record_id: props.modal_config.record_id,
      });

      emit("close-modal");
    } else {
      let invalid_tab = _continue.invalid_groups[0]
        ? props.modal_config.tabs.find((tab) => tab.label === _continue.invalid_groups[0])
        : props.modal_config.tabs[0];

      activeProfileTab.value = invalid_tab.slot_name;

      await handleActivityLog(false, _continue);

      return;
    }
  } catch (e) {
    var alert = {
      type: "error",
      message: `Failed to create beneficial owner.`,
    };
    UIStore.animateNotificationAlert(alert);
    emit("close-modal");
  }
}
function getStateObject(group) {
  if (group == "ID Information") {
    return props.modal_config.object_to_edit.id_info
      ? props.modal_config.object_to_edit.id_info
      : {};
  }
  return props.modal_config.object_to_edit;
}
function checkForChanges(object) {
  let original;
  let current;

  if (object === "id_info") {
    original = props.modal_config.original_id_info;
    current = props.modal_config.id_info;
  } else {
    original = props.modal_config.original_object;
    current = props.modal_config.object_to_edit;
  }

  const changes = {};

  function isObject(value) {
    return value !== null && typeof value === "object";
  }

  function deepEqual(a, b) {
    if (a === b) return true;

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (!isObject(a) || !isObject(b)) {
      return false;
    }

    if (Array.isArray(a) !== Array.isArray(b)) {
      return false;
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) {
      return false;
    }

    for (const key of aKeys) {
      if (!bKeys.includes(key)) {
        return false;
      }

      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }

    return true;
  }

  function findChanges(originalObj, currentObj, path = "") {
    const allKeys = new Set([...Object.keys(originalObj || {}), ...Object.keys(currentObj || {})]);

    for (const key of allKeys) {
      const fullPath = path ? `${path}.${key}` : key;
      const oldValue = originalObj?.[key];
      const newValue = currentObj?.[key];

      const bothAreObjects =
        isObject(oldValue) &&
        isObject(newValue) &&
        !(oldValue instanceof Date) &&
        !(newValue instanceof Date);

      if (bothAreObjects) {
        if (!deepEqual(oldValue, newValue)) {
          findChanges(oldValue, newValue, fullPath);
        }
      } else if (!deepEqual(oldValue, newValue)) {
        changes[fullPath] = {
          old_value: oldValue,
          new_value: newValue,
        };
      }
    }
  }

  findChanges(original, current);

  const changed = Object.keys(changes).length > 0;

  if (changed) {
    // console.log("Changed fields:", changes);
  }

  return changed;
}
async function handleActivityLog(valid, validation_details) {
  let status = valid ? "Completed" : "Failed Validation";

  let log = {
    step: "Profile Page",
    substep: "Edit Beneficial Owner",
    status: status,
    message: "Editing Beneficial Owner with ID: " + props.modal_config.record_id,
    url: window.location.href,
    validation_details: valid ? null : JSON.stringify(validation_details),
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
